import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { PageContext } from '../../../lib/context';
import queries from '../../../utils/gql/queries';
import PaymentsPage from './payments-view';
import { Form, message, Popconfirm } from 'antd';
import moment from 'moment';
import mutations from '../../../utils/gql/mutations';

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  name: string;
  barangay: string;
  created_at: Date;
  comsumer_type: number;
  complete_address: string;
  meter_id: number;
}
interface Billings {
  fees: number;
  id: number;
  advance_payment: number;
  previous_balance: number;
  consumption: number;
  total_balance: number;
}

interface Payments {
  amount: number
  billing_id: number
  date: Date
  id: number
  subscribers_id: number
  transacted_by: number
}
interface Sort {
  id: string | null | boolean;
  subscribers_id: string | null | boolean;
  subscriber: Subscriber;
  billing_id: string | null | boolean;
  bill: Total_Balance;
  amount: string | null | boolean;
  date: string | null | boolean;
  transacted_by: string | null | boolean;
}

interface Subscriber {
  first_name: string | null | boolean;
}

interface Total_Balance {
  total_balance: string | null | boolean;
}

const PaymentsViewController = () => {
  const [payments, setPayments] = useState<Payments[]>([]);
  const [searchPay, setSearchPay] = useState(null);
  const [total, setTotal] = useState<number>();
  const [limit, setLimit] = useState<number>(10);
  const [offset, setOffset] = useState<number>(0);
  const [current, setCurrent] = useState<number>(1);
  const [sort, setSort] = useState<any>(null);
  const [where, setWhere] = useState<any>(null);
  const [sortOrder, setSortOrder] = useState<Sort>({
    id: false,
    subscribers_id: false,
    billing_id: null,
    bill: {
      total_balance: null,
    },
    amount: null,
    date: null,
    transacted_by: null,
    subscriber: {
      first_name: null,
    }
  });

  const [getPayments, { data: payment_search, loading }] = useLazyQuery(
    queries.GET_PAYMENTS,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const { data: total_payment } = useQuery(queries.GET_TOTAL_PAYMENTS, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (total_payment) {
      setTotal(total_payment?.payments_aggregate?.aggregate?.count)
    }
  }, [total_payment]);

  const handleSort = (pagination: any, filters: any, sorter: any) => {
    const sorterOrder: any = {
      ascend: 'asc',
      descend: 'desc'
    }
    if (sorter.columnKey === 'name') {
      setSort({
        subscriber: {
          first_name: sorterOrder[sorter.order]
        }
      });
      setSortOrder({
        ...sortOrder,
        subscriber: {
          first_name: sorter.order
        }
      })
    } else if (sorter.columnKey === 'total_balance') {
      setSort({
        bill: {
          total_balance: sorterOrder[sorter.order]
        }
      });
      setSortOrder({
        ...sortOrder,
        bill: {
          total_balance: sorter.order
        }
      })
    } else {
      setSort({
        [sorter.columnKey]: sorterOrder[sorter.order]
      });
      setSortOrder({ ...sortOrder, [sorter.columnKey]: sorter.order });
    }
  };

  useEffect(() => {
    _getPayments()
  }, [sort]);

  const [deleteRecord] = useMutation(mutations.DELETE_PAYMENT, {
    onCompleted(res) {
      message.success('Payment deleted');
      _getPayments();
    },
    onError({ graphQLErrors }) {
      message.error('An error occured');
    },
  });

  const usersColumns = [
    {
      title: 'Payment ID',
      key: 'id',
      render: (item: any) => {
        return subs_id(item.id);
      },
      sorter: true,
      sortOrder: sortOrder.id
    },
    {
      title: 'Customer ID',
      key: 'subscribers_id',
      render: (item: any) => {
        return subs_id(item.subscribers_id);
      },
      sorter: true,
      sortOrder: sortOrder.subscribers_id,
    },
    {
      title: 'Name',
      key: 'name',
      render: (item: any) => {
        return `${item.subscriber.first_name} ${item.subscriber.last_name}`;

      },
      sorter: true,
      sortOrder: sortOrder.subscriber.first_name
    },
    {
      title: 'Bill ID',
      key: 'billing_id',
      render: (item: any) => {
        return subs_id(item.billing_id);
      },
      sorter: true,
      sortOrder: sortOrder.billing_id
    },
    {
      title: 'Bill Total Balance',
      key: 'total_balance',
      render: (item: any) => (
        Math.round(item?.bill.total_balance * 100) / 100
      ),
      sorter: true,
      sortOrder: sortOrder.bill?.total_balance
    },

    {
      title: 'Paid Amount',
      dataIndex: 'amount',
      key: 'amount',
      sorter: true,
      sortOrder: sortOrder.amount
    },

    {
      title: 'Paid Date',
      key: 'date',
      render: (item: any) => (
        moment(`${item?.date}`).format('LL')
      ),
      sorter: true,
      sortOrder: sortOrder.date
    },

    {
      title: 'Transacted by',
      key: 'transacted_by',
      render: (item: any) => {
        return `${item?.users?.full_name}`;
      },
      sorter: true,
      sortOrder: sortOrder.transacted_by
    },
    {
      title: '',
      key: 'action',
      render: (record: any) => {
        return (
          <Popconfirm
            title="Are you sure to delete this payment?"
            onConfirm={() =>
              deleteRecord({
                variables: {
                  id: record.id,
                },
              })
            }
            onCancel={() => message.error('Action cancelled!')}
            okText="Yes"
            cancelText="No"
          >
            <a href="#">Delete</a>
          </Popconfirm>
        );
      },
    },
  ];

  useEffect(() => {
    if (payment_search) {
      const data = payment_search.payments;
      setSearchPay(data);
      setPayments(data);
    }
  }, [payment_search]);

  const _onFinishSearch = (data: any) => {
    let where: any = {};
    if (data.id && data.id !== '') {
      let _or: any = [
        {
          billing_id: { _eq: data?.id ? parseInt(data.id) : null },
        },
        {
          subscribers_id: { _eq: data?.id ? parseInt(data.id) : null },
        },
      ];

      if (isNaN(data.id)) {
        _or = [
          {
            subscriber: {
              first_name: {
                _ilike: `%${data.id}%`,
              },
            },
          },
          {
            subscriber: {
              last_name: {
                _ilike: `%${data.id}%`,
              },
            },
          },
        ];
      }

      where = {
        ...where,
        _or,
      };
      setWhere(where);
    } else {
      setWhere(null);
    }
    setPayments([]);
  };

  useEffect(() => {
    _getPayments();
  }, [where]);

  const _getPayments = () => {
    getPayments({
      variables: {
        where,
        limit: limit,
        offset: offset,
        order_by: sort,
      }
    });
  };

  useEffect(() => {
    _getPayments();
  }, [getPayments]);

  const subs_id = (id: number): String => {
    var sub_id = `${id}`;
    while (sub_id.length < 7) {
      sub_id = `0${sub_id}`;
    }
    return sub_id;
  };

  const handlePaginationChange = (page: any, pageSize: any) => {
    setLimit(pageSize);
    setCurrent(page);
    if (page * pageSize > pageSize) {
      setOffset(page * pageSize - pageSize);
    }
    if (page === 1) {
      setCurrent(page);
      setOffset(0);
    }
  };

  useEffect(() => {
    if (offset || current) {
      _getPayments();
    }
  }, [offset, current]);

  const value: object = {
    usersColumns,
    payments,
    // searchForm,
    searchPay,
    loading,
    limit,
    offset,
    total,
    current,
    handlePaginationChange,
    handleSort,
    _onFinishSearch,
  };

  return (
    <PageContext.Provider value={value}>
      <PaymentsPage />
    </PageContext.Provider>
  );
};

export default PaymentsViewController;
